import { Download, Mail, Printer, Send, XCircle } from "lucide-react";
import React, { useMemo } from "react";
import { Button, Separator } from "randmarcomps";
import { useUser } from "../../../hooks/useUser";
import CancelPurchaseOrder from "./CancelPurchaseOrder";
import CancelRequisition from "./CancelRequisition";
import GeneratePDFWrapper from "./GeneratePDFWrapper";
import SendByEmailWrapper from "./SendByEmailWrapper";
import SendPurchaseOrderToSupplier from "./SendPurchaseOrderToSupplier";

interface IProps {
  documentNumber: string;
}

const DocumentActions: React.FC<IProps> = ({ documentNumber }) => {
  const { isSuperAdmin } = useUser();

  const isPurchaseOrder = useMemo(
    () => documentNumber.startsWith("PO") && documentNumber.length === 9,
    [documentNumber]
  );
  const isRequisition = useMemo(
    () => documentNumber.startsWith("RQ") && documentNumber.length === 9,
    [documentNumber]
  );

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">General</p>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <GeneratePDFWrapper documentNumber={documentNumber} action="open">
            <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
              <Printer className="mr-2 h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
              Print
            </Button>
          </GeneratePDFWrapper>
          <GeneratePDFWrapper documentNumber={documentNumber} action="download">
            <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
              <Download className="mr-2 h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
              Download
            </Button>
          </GeneratePDFWrapper>
          <SendByEmailWrapper documentNumber={documentNumber}>
            <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
              <Mail className="mr-2 h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
              Send by Email
            </Button>
          </SendByEmailWrapper>
        </div>
      </div>

      <Separator className="my-1" />

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{documentNumber}</p>

        {isPurchaseOrder && (
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <CancelPurchaseOrder purchaseOrderNumber={documentNumber}>
              <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
                <XCircle className="mr-2 h-4 w-4 text-rose-600" aria-hidden="true" />
                Cancel Order
              </Button>
            </CancelPurchaseOrder>
            {isSuperAdmin && (
              <SendPurchaseOrderToSupplier purchaseOrderNumber={documentNumber}>
                <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
                  <Send className="mr-2 h-4 w-4 text-emerald-600" aria-hidden="true" />
                  Send PO to Manufacturer
                </Button>
              </SendPurchaseOrderToSupplier>
            )}
          </div>
        )}
        {isRequisition && (
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <CancelRequisition requisitionNumber={documentNumber}>
              <Button variant="ghost" className="w-full justify-start rounded-none px-3 py-2.5 text-sm font-medium hover:bg-gray-50">
                <XCircle className="mr-2 h-4 w-4 text-rose-600" aria-hidden="true" />
                Cancel Draft
              </Button>
            </CancelRequisition>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentActions;
